// TOTP 중복 감지 레지스트리(PR8 #1506) — 세션 누적 지문 저장소.
//
// 제로지식 제약: 서버는 secret 암호문만 보유하고 암호화가 랜덤 salt 기반이라 동일
// secret 도 암호문이 달라 서버 측 중복 감지가 불가능하다(pUtils encryptAESByKey 실측).
// 대신 허브 탭 카드들이 표시를 위해 이미 수행하는 복호 결과에서 SHA-256 지문만 뽑아
// 여기에 누적한다 — 평문 secret 은 저장하지 않고, 추가 복호 비용도 없다.
//
// 세션 누적 설계: 페이지를 넘겨도 방문분이 쌓여 페이지 경계(100건) 너머의 중복도
// 점진 감지되며, 저장 시(RecordEdit 확인 팝업) 캐시 조회로도 겸용된다.
// 로그아웃(removeAllAppUserKeys)에서 clearAll 로 계정 전환 잔존을 방지한다.
//
// node-forge 는 pUtils 에서만 쓰이던 의존이 services/totp 계열에 처음 진입하는
// 것이지만, 번들에 이미 포함(크기 영향 0)이고 CJS 라 jest 파싱도 안전하다.
// sync 해시가 필요해(Web Crypto subtle.digest 는 async — 저장 경고 동기 흐름 복잡)
// forge.md.sha256 을 사용한다.
import forge from 'node-forge'

import { extractSecret } from './index'

// recordId → { fp, title }
const entries = new Map()
const listeners = new Set()
// findDuplicates 스냅샷 캐시 — useSyncExternalStore 는 변경 없을 때 동일 참조를
// 요구한다(매번 새 배열이면 무한 리렌더). version 이 같으면 캐시를 재사용.
let version = 0
const snapshotCache = new Map() // recordId → { version, result }

const EMPTY = Object.freeze([])

function normalizeSecret(raw) {
  return String(raw).replace(/\s+/g, '').replace(/=+$/, '').toUpperCase()
}

// otpauth URI 또는 bare secret → SHA-256 hex 지문. 무효 입력은 null(등록/조회 무시).
// 폴백 골격은 getTotpSecretLength(services/totp/index.js) 답습, 정규화는 저장 시
// 정제 규칙(toSecret — 공백/패딩 제거+대문자화)과 동일 — 표기만 다른 같은 키가 같은
// 지문이 되도록.
export function totpFingerprint(uriOrSecret) {
  if (!uriOrSecret) return null
  const raw = String(uriOrSecret).trim()
  if (!raw) return null

  let secret = raw
  if (raw.toLowerCase().startsWith('otpauth://')) {
    try {
      secret = extractSecret(raw)
    } catch (err) {
      return null // 파싱 불가 URI — 판단 불가 시 미등록
    }
  }
  const normalized = normalizeSecret(secret)
  if (!normalized) return null

  const md = forge.md.sha256.create()
  md.update(normalized)
  return md.digest().toHex()
}

function notify() {
  version += 1
  listeners.forEach((cb) => {
    try {
      cb()
    } catch (e) {
      // 리스너 오류가 다른 구독자 통지를 막지 않도록 격리
    }
  })
}

export function register(recordId, fp, title) {
  if (recordId == null || fp == null) return
  const cur = entries.get(recordId)
  if (cur && cur.fp === fp && cur.title === title) return // 무변경 — 통지 생략
  entries.set(recordId, { fp, title })
  notify()
}

export function unregister(recordId) {
  if (!entries.has(recordId)) return
  entries.delete(recordId)
  snapshotCache.delete(recordId) // 삭제 레코드의 스냅샷 잔존 방지(PR #1510 리뷰)
  notify()
}

// 같은 지문을 가진 다른 레코드 목록 — [{recordId, title}].
// useSyncExternalStore 의 getSnapshot 으로 직접 쓸 수 있도록 참조 안정성을 보장한다.
export function findDuplicates(recordId) {
  const cached = snapshotCache.get(recordId)
  if (cached && cached.version === version) return cached.result

  const self = entries.get(recordId)
  let result = EMPTY
  if (self) {
    const found = []
    entries.forEach((entry, id) => {
      if (id !== recordId && entry.fp === self.fp) {
        found.push({ recordId: id, title: entry.title })
      }
    })
    if (found.length > 0) result = found
  }
  // 내용이 이전 스냅샷과 동등하면 이전 참조 유지(불필요 리렌더 방지)
  if (
    cached &&
    cached.result.length === result.length &&
    cached.result.every((v, i) => v.recordId === result[i].recordId && v.title === result[i].title)
  ) {
    result = cached.result
  }
  snapshotCache.set(recordId, { version, result })
  return result
}

// 저장 시 캐시 조회 — 같은 지문의 다른 레코드 1건({recordId, title}) 또는 null.
export function hasFingerprint(fp, excludeRecordId) {
  if (fp == null) return null
  let found = null
  entries.forEach((entry, id) => {
    if (found == null && id !== excludeRecordId && entry.fp === fp) {
      found = { recordId: id, title: entry.title }
    }
  })
  return found
}

export function subscribe(cb) {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

export function clearAll() {
  if (entries.size === 0 && snapshotCache.size === 0) return
  entries.clear()
  snapshotCache.clear()
  notify()
}
